import { memo, useRef, useState } from "react";
import { Link, LinkButton, LinkType } from "../../../common/components/Link";
import { Icon } from "../../../common/components/icon/Icon";
import { IconType } from "../../../common/components/icon/IconType";
import { IconSize } from "../../../common/components/icon/IconSize";
import { useBooleanState } from "../../../common/hooks/useBooleanState";
import LoginPopup from "../../../Login/components/LoginPopup";
import ResetPasswordPopup from "../../../Login/components/ResetPasswordPopup";
import { isForcedLogin, isLoginPopupInitiallyOpen, isLoginTimeoutErrorVisible } from "../../../Login/loginUtils";
import ToastMessage from "../../../common/components/Messages/ToastMessage/ToastMessage";
import { UserHeaderCircle } from "./HeaderCircle";

import useMediaQuery from "../../../common/hooks/useMediaQuery";
import { REGISTER_PAGE } from "../../../utils/url/staticUrls";
import { MEDIA_QUERY_MIN_DESKTOP_S, MEDIA_QUERY_MIN_TABLET } from "../../../utils/screenSizes";
import { useTranslation } from "react-i18next";

import { Box, ClickAwayListener, menuClasses, styled } from "@mui/material";
import { Menu } from "~/common/components/Menu/Menu";
import { Button } from "~/common/components/Button/Button";
import classes from "./HeaderLoginElement.module.less";

export const HeaderLoginElement = memo(() => {
  const { t } = useTranslation();

  const { val: menu, setTrue: openMenu, setFalse: closeMenu } = useBooleanState(false);

  const { val: delay, setTrue: setDelay, setFalse: clearDelay } = useBooleanState(false);

  const timerRef = useRef<number | null>(null);

  const {
    val: loginPopupOpen,
    setTrue: openLogin,
    setFalse: closeLogin,
  } = useBooleanState(() => isLoginPopupInitiallyOpen());
  const {
    val: resetPopupOpen,
    setTrue: openReset,
    setFalse: closeReset,
  } = useBooleanState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("reset")?.toLowerCase() === "true";
  });

  const [email, setEmail] = useState("");

  const onResetClick = (email: string) => {
    setEmail(email);
    closeLogin();
    openReset();
  };

  const isTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const anchorEl = useRef<HTMLButtonElement>(null);

  const handleSignInClick = () => {
    closeMenu();
    openLogin();
  };

  let element;
  if (isTablet) {
    element = (
      <ClickAwayListener onClickAway={closeMenu}>
        <Box
          sx={{
            gap: 2,
            alignItems: "center",
            display: "flex"
          }}>
          <LinkButton
            ref={anchorEl}
            className={classes.userButton}
            onClick={() => {
              if (menu) {
                closeMenu();
              }
              openLogin();
            }}
            onMouseEnter={() => {
              if (timerRef.current) {
                clearTimeout(timerRef.current);
              }
              timerRef.current = window.setTimeout(() => {
                if (!delay) {
                  openMenu();
                }
              }, 100);
            }}
            onMouseLeave={() => {
              if (timerRef.current) {
                clearTimeout(timerRef.current);
              }
            }}
            variant={LinkType.NAVIGATION}
          >
            <UserHeaderCircle variant={"general"}>
              <Icon type={IconType.User} size={isDesktop ? IconSize.FONT_30 : IconSize.FONT_24} />
            </UserHeaderCircle>
            <Icon
              className={"icon-margin-l scha__header__language--icon"}
              type={menu ? IconType.ChevronUp : IconType.ChevronDown}
              size={IconSize.FONT_20}
            />
          </LinkButton>

          <StyledMenu
            anchorEl={anchorEl.current}
            open={menu}
            hideBackdrop={true}
            onClose={() => {
              closeMenu();
              setDelay();
              const intervalHandler = setInterval(() => {
                window.clearInterval(intervalHandler);
                clearDelay();
              }, 350) as unknown as number;
            }}
          >
            <div className={classes.menuContainer}>
              <Button fullWidth onClick={handleSignInClick}>
                {t("header.link.signIn")}
              </Button>
              <div className={classes.orElement}>
                <span className={classes.orText}>{t("common.or")}</span>
              </div>

              <Link className={classes.registerLink} href={REGISTER_PAGE}>
                {t("header.link.register")}
              </Link>
            </div>
          </StyledMenu>
        </Box>
      </ClickAwayListener>
    );
  } else {
    element = (
      <button className={"btn-style-reset scha__mobile-navigation__button"} onClick={openLogin}>
        <UserHeaderCircle variant={"general"}>
          <Icon type={IconType.User} size={IconSize.FONT_24} />
        </UserHeaderCircle>
      </button>
    );
  }

  return (
    <>
      {element}

      {(loginPopupOpen || (isForcedLogin() && !resetPopupOpen)) && (
        <LoginPopup initialEmail={""} onResetClick={onResetClick} close={closeLogin} />
      )}
      {resetPopupOpen && <ResetPasswordPopup initialEmail={email} close={closeReset} />}
      {isLoginTimeoutErrorVisible() && <ToastMessage type={"info"}>{t("login.error.timeout")}</ToastMessage>}
    </>
  );
});

const StyledMenu = styled(Menu)({
  pointerEvents: "none",
  [`& .${menuClasses.paper}`]: {
    pointerEvents: "all",
  },
});
